<template>
  <section id="agreement-setup">
    <div v-if="loading" class="loading-container">
      <img src="@shared/assets/img/general/loading.svg" alt="loading" />
    </div>
    <div v-else class="content-container">
      <div class="ac-order-card container just-center ac-card-content">
        <div class="padding-default flex-row align-center just-between">
          <div class="sac__font-22 med">Review Agreement</div>
          <div class="pos-relative">
            <button
              v-if="psm?.id"
              type="button"
              class="sac__font-16 f-primary underline bold psm-button"
              @click="showPopup = !showPopup"
            >
              Questions? Contact your PSM here.
            </button>
            <PSMContactPopup
              v-if="showPopup"
              :psm="psm"
              :close="() => (showPopup = false)"
            />
          </div>
        </div>
        <div class="ac-body-container grid-row gap-16">
          <div class="grid-row gap-16">
            <div v-if="showMSASection" class="bordered-card">
              <div class="sac__font-16 semi-bold mb-2">MSA Initial Payment</div>
              <div>
                <div class="grid-column first-large-3 sac__font-14 med margin-top-8">
                  <div class="grid-item js-s">Item</div>
                  <div class="grid-item js-c">Quantity</div>
                  <div class="grid-item js-e">Total</div>
                </div>
                <div class="order-detail-container">
                  <div
                    v-for="(order, idx) in agreementOrders"
                    :key="order.agreementOrderId"
                    class="ord-row"
                  >
                    <div class="ao-table-body">
                      <div class="grid-item js-s">
                        <span class="sac__font-14 med"> Order {{ idx + 1 }}: </span>
                        SmartAC.com Detect Subscription Monitoring
                        <div>Initial payment</div>
                      </div>
                      <div class="grid-item js-c">
                        {{ agreementMembership(order)?.quantity }}
                      </div>
                      <div class="grid-item js-e">
                        ${{ getNumberLocale(agreementMembership(order)?.total) }}
                      </div>
                    </div>
                  </div>
                  <OrderLineItem
                    name="SmartAC.com Hardware Kit"
                    :quantity="totalHardware"
                    total="Included in Bundle"
                  />
                  <OrderLineItem
                    bold
                    darkRow
                    name="PAID"
                    quantity=""
                    :total="`$${getNumberLocale(totalDeposit)}`"
                  />
                </div>
              </div>
              <div v-if="branchOptions.length > 1" class="flex-column dropdown">
                <div class="sac__font-14 mt-3">Bill MSA initial payment to:</div>
                <SACDropdown
                  class="dropdown mt-2"
                  field="msaPayee"
                  placeholder="Select Payee"
                  :value="payee.msaPayee"
                  :options="branchOptions"
                  :change="onChange"
                  :error="error.msaPayee"
                  :disable="initialPaymentPaid"
                />
              </div>
            </div>
            <div
              v-for="(order, idx) in agreementOrders"
              :key="order.agreementOrderId"
              class="bordered-card"
            >
              <div
                v-if="hardwareOrderline(order)?.length > 0 || agreementMembership(order)"
                class="mt-2 mb-4"
              >
                <div class="grid-column ai-c jc-b sac__font-18 semi-bold">
                  <div>Order {{ idx + 1 }} Total</div>
                </div>
                <div class="mt-2">
                  <div class="grid-column first-large-3 med sac__font-14 med">
                    <div class="grid-item js-s">Item</div>
                    <div class="grid-item js-c">Quantity</div>
                    <div class="grid-item js-e">Total</div>
                  </div>
                  <OrderLineItem
                    v-if="agreementMembership(order)"
                    name="SmartAC.com Detect Subscription Monitoring"
                    :quantity="agreementMembership(order).quantity"
                    :total="`$${getNumberLocale(agreementMembership(order).total)}`"
                  />
                  <OrderLineItem
                    v-if="agreementMembership(order)"
                    name="MSA Initial Payment applied"
                    quantity="-"
                    :total="`($${getNumberLocale(agreementMembership(order).total)})`"
                  />
                  <OrderLineItem
                    v-for="(ol, i) in hardwareOrderline(order)"
                    :key="i"
                    :name="ol.itemName"
                    :quantity="ol.quantity"
                    :total="
                      ol.total > 0
                        ? `$${getNumberLocale(ol.total)}`
                        : 'Included in Bundle'
                    "
                  />
                  <OrderLineItem
                    v-if="order.shippingCost"
                    :name="`Shipping to ${shippingAddress(order)}`"
                    :total="`$${getNumberLocale(order.shippingCost)}`"
                  />
                  <OrderLineItem
                    v-if="orderPaid(order)"
                    bold
                    darkRow
                    name="PAID"
                    quantity=""
                    :total="`$${getNumberLocale(agreementTotal(order))}`"
                  />
                  <OrderLineItem
                    v-else
                    bold
                    :name="`Total due on ${getFullYear(order.plannedStartDate)}`"
                    quantity=""
                    :total="`$${getNumberLocale(agreementTotal(order))}`"
                  />
                </div>
              </div>
              <template v-if="hasSubscription(order)">
                <div class="grid-column ai-c jc-b sac__font-18 semi-bold">
                  <div>Order {{ idx + 1 }} Subscriptions</div>
                </div>
                <div class="grid-column first-large-3 med sac__font-14 med">
                  <div class="grid-item js-s">Item</div>
                  <div class="grid-item js-c">Quantity</div>
                  <div class="grid-item js-e">Total</div>
                </div>
              </template>
              <OrderLineItem
                v-if="platformFee(order)"
                name="SmartAC.com Platform Fee"
                quantity="1"
                :total="`$${getNumberLocale(platformFee(order).total)}/month`"
              />
              <OrderLineItem
                v-if="connectMembership(order)"
                name="SmartAC.com Connect Subscription"
                :quantity="
                  connectMembership(order)?.freeQuantity > 0
                    ? `${connectMembership(order).freeQuantity} free user(s)`
                    : '-'
                "
                :total="
                  connectMembership(order).total > 0
                    ? `$${getNumberLocale(connectMembership(order).total)} per user`
                    : 'Free'
                "
              />
              <OrderLineItem
                v-if="agreementMembership(order)"
                name="SmartAC.com Detect Subscription Monitoring"
                :quantity="agreementMembership(order).quantity"
                :total="`$${getNumberLocale(agreementMembership(order).total)}/month`"
              />
              <OrderLineItem
                v-if="platformFee(order)"
                bold
                :name="`Platform Subscription charge starts (${getFullYear(
                  order.plannedStartDate
                )})`"
                quantity="-"
                :total="`$${getNumberLocale(platformFee(order).total)}`"
              />
              <OrderLineItem
                v-if="agreementMembership(order)"
                bold
                :name="`Detect Subscription charge resuming (${subscriptionDate(order)})`"
                :quantity="agreementMembership(order).quantity"
                :total="`$${getNumberLocale(agreementMembership(order).total)}`"
              />
            </div>
            <div v-if="branchOptions.length > 1" class="flex-row mt-3">
              <div class="flex-column dropdown">
                <div class="sac__font-14 mt-3">Bill order initial payment to:</div>
                <SACDropdown
                  class="dropdown mt-2"
                  :field="`orderPayee-${order.agreementOrderId}`"
                  placeholder="Select Payee"
                  :value="payee[`orderPayee-${order.agreementOrderId}`]"
                  :options="branchOptions"
                  :change="onChange"
                  :error="error[`orderPayee-${order.agreementOrderId}`]"
                  :disable="orderPaid(order)"
                />
              </div>
              <div class="flex-column ml-4 dropdown">
                <div class="sac__font-14 mt-3">Bill subscription to:</div>
                <SACDropdown
                  class="dropdown mt-2"
                  :field="`subscriptionPayee-${order.agreementOrderId}`"
                  placeholder="Select Payee"
                  :value="payee[`subscriptionPayee-${order.agreementOrderId}`]"
                  :options="branchOptions"
                  :change="onChange"
                  :error="error[`subscriptionPayee-${order.agreementOrderId}`]"
                />
              </div>
            </div>
          </div>
          <div class="footer-container jc-e flex-row just-end">
            <button type="button" class="sac__button bg-primary" @click="next">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    <ActionModal />
  </section>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from "vuex";

import partnerAgreementMixin from "@shared/mixins/partnerAgreementMixin";
import unitPreferencesMixin from "@shared/mixins/unitPreferencesMixin";
import ActionModal from "@shared/components/partnerAgreement/ActionModal.vue";
import SACDropdown from "@shared/components/sac/SACDropdown.vue";
import OrderLineItem from "@shared/components/partnerAgreement/OrderLineItem.vue";
import PSMContactPopup from "@shared/components/partnerAgreement/PSMContactPopup.vue";

export default {
  name: "ReviewView",
  components: {
    ActionModal,
    SACDropdown,
    OrderLineItem,
    PSMContactPopup,
  },
  mixins: [partnerAgreementMixin, unitPreferencesMixin],
  data() {
    return {
      payee: {},
      error: {},
      loading: true,
      showPopup: false,
    };
  },
  computed: {
    ...mapState({
      agreement: (state) => state.partnerAgreement.agreement,
      psm: (state) => state.partnerAgreement.psm,
      partnerBranches: (state) => state.partnerAgreement.partnerBranches,
    }),
    ...mapGetters(["allBranches", "totalDeposit", "isSmacAdmin"]),
    showMSASection() {
      return this.agreement?.agreementOrders.some((order) =>
        this.agreementMembership(order)
      );
    },
    totalHardware() {
      return this.agreementOrders.reduce((acc, order) => {
        let total = acc;
        if (this.agreementMembership(order)) {
          total += this.agreementMembership(order).quantity;
        }
        return total;
      }, 0);
    },
    agreementOrders() {
      if (this.agreement?.agreementOrders) {
        let isPaid = false;
        const orders = [];
        this.agreement.agreementOrders.forEach((o) => {
          const { orderStatus } = o;
          if (orderStatus?.toLowerCase() === "payment pending" && !isPaid) {
            orders.push(o);
          } else if (orderStatus?.toLowerCase() !== "payment pending") {
            isPaid = true;
            orders.push(o);
          }
        });
        return orders;
      }
      return [];
    },
    branchOptions() {
      return this.partnerBranches.map((b) => ({
        label: b.corporationName,
        value: b.id,
      }));
    },
    initialPaymentPaid() {
      let paid = false;
      this.agreementOrders.forEach((o) => {
        const { orderStatus } = o;
        if (orderStatus !== "Payment Pending") {
          paid = true;
        }
      });
      return paid;
    },
  },
  watch: {
    agreement(ag) {
      const { depositCorpId, agreementOrders } = ag;
      if (agreementOrders) {
        const payee = {};
        payee.msaPayee = depositCorpId;
        for (let i = 0; i < agreementOrders.length; i += 1) {
          const {
            agreementOrderId,
            depositCorpId: orderDepositCorpId,
            subscriptionCorpId,
          } = agreementOrders[i];
          payee[`subscriptionPayee-${agreementOrderId}`] = subscriptionCorpId;
          payee[`orderPayee-${agreementOrderId}`] = orderDepositCorpId;
        }
        this.payee = payee;
      }
    },
  },
  async mounted() {
    await this.$store.dispatch("getPartnerAgreement", this.$route.params.agreementId);
    this.loading = false;
  },
  methods: {
    subscriptionDate(order) {
      const { startDate, plannedStartDate, deploymentPeriod } = order;
      const date = startDate || plannedStartDate;
      return this.fullDateFormat(moment(date).add("days", deploymentPeriod));
    },
    agreementTotal(order) {
      const olTotal = order.agreementOrderLines.slice(1).reduce((acc, ol) => {
        let total = acc;
        if (!ol.subscriptionPlanId) {
          total += ol.total;
        }
        return total;
      }, 0);
      return olTotal + order.shippingCost;
    },
    hardwareOrderline(order) {
      return order.agreementOrderLines.filter((ol) => !ol.subscriptionPlanId);
    },
    agreementMembership(order) {
      return order.agreementOrderLines.find((ol) =>
        ol.subscriptionPlanId?.includes("PM")
      );
    },
    platformFee(order) {
      return order.agreementOrderLines.find((ol) =>
        ol.subscriptionPlanId?.includes("PLAT")
      );
    },
    connectMembership(order) {
      return order.agreementOrderLines.find((ol) =>
        ol.subscriptionPlanId?.includes("CNX")
      );
    },
    hasSubscription(order) {
      return Boolean(
        this.platformFee(order) ||
          this.connectMembership(order) ||
          this.agreementMembership(order)
      );
    },
    shippingAddress(order) {
      const {
        shippingAddress: { address1, address2, city, state, zip },
      } = order;
      return `${address1}${address2 ? ` ${address2}` : ""}, ${city}, ${state} ${zip}`;
    },
    orderPaid(order) {
      const { orderStatus, startDate } = order;
      if (startDate) {
        const startDatePassed = moment().isAfter(moment(startDate));
        if (orderStatus !== "Payment Pending" && startDatePassed) {
          return true;
        }
      }
      return false;
    },
    next() {
      const errorObj = {};
      const newPayee = this.payee;
      if (this.branchOptions.length > 1) {
        Object.entries(this.payee).forEach((el) => {
          const [key, value] = el;
          if (value == null) {
            errorObj[key] = "Please select a payee";
          }
        });
      } else {
        Object.keys(this.payee).forEach((key) => {
          newPayee[key] = this.branchOptions[0].value;
        });
      }
      this.error = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.$store.dispatch("changePartnerAgreementValues", [
          {
            field: "payee",
            value: newPayee,
          },
        ]);
        this.$router.push(`/agreement/${this.$route.params.agreementId}/payment/update`);
      }
    },
    toggleActionModal(show, view) {
      if (show) {
        this.$store.dispatch("changePartnerAgreementValues", [
          {
            field: "showActionModal",
            value: show,
          },
          {
            field: "actionModalView",
            value: view,
          },
        ]);
      } else {
        this.$store.dispatch("changePartnerAgreementValues", [
          {
            field: "showActionModal",
            value: show,
          },
        ]);
      }
    },
    onChange(field, value) {
      this.payee[field] = value;
    },
  },
};
</script>

<style lang="scss">
section#agreement-setup {
  @include section-container;
  background-color: $WHITE;
  .loading-container {
    flex: 1;
  }
  .ao-table-order-body {
    margin-top: 8px;
    display: grid;
    padding: 8px;
    grid-template-columns: 1fr 1fr 1fr;
    background-color: $NBLUE15;

    &:nth-child(even) {
      background-color: $WHITE;
    }
  }

  .order-detail-container {
    margin-top: 5px;
  }

  .order-detail-container .deposit-container {
    padding: 8px;
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    background-color: #002e63;
    color: $WHITE;
    font-size: 14px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .bordered-card {
    border-radius: 10px;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 15px;
    padding-bottom: 10px;
    border: 1.5px solid $N30;
  }

  .subscriptions {
    background-color: $NBLUE15;
    border-radius: 4px;
    display: grid;
    padding: 8px;
    grid-template-columns: 1fr 1fr 1fr;
  }

  .dropdown {
    width: 300px;
  }

  .container {
    background-color: $WHITE;
    width: 100%;
  }
}
</style>
